import isPlainObject from 'lodash/isPlainObject';
import lowerCase from 'lodash/lowerCase';
import upperFirst from 'lodash/upperFirst';
import { formatCurrency } from 'utils/formatters/formatCurrency';
import type { SnapshotField } from 'api/admin/orders/getOrderPipelineAudit';
import {
  CENTS_IN_A_DOLLAR,
  CENT_FIELDS,
  DETAIL_DOLLAR_KEYS,
  DOLLAR_FIELDS,
  EMPTY_LIST,
  NOT_RECORDED,
  SNAPSHOT_FIELD_LABELS,
} from 'constants/order/orderPipelineAudit';

/**
 * Render one snapshot field for display.
 *
 * `null` and `undefined` both mean "not recorded" — the backend normalises absent DataDog attributes
 * back to null, and a field can also be missing outright.
 */
export const formatSnapshotValue = (field: SnapshotField, value: unknown): string => {
  if (value === null || value === undefined) {
    return NOT_RECORDED;
  }

  if (Array.isArray(value)) {
    // An empty list means none were applied — distinct from a value that was never recorded.
    return value.length ? value.join(', ') : EMPTY_LIST;
  }

  if (typeof value === 'number') {
    if (DOLLAR_FIELDS.has(field)) {
      return formatCurrency(value);
    }

    if (CENT_FIELDS.has(field)) {
      return formatCurrency(value / CENTS_IN_A_DOLLAR);
    }

    return String(value);
  }

  return String(value);
};

/**
 * Turn a camelCase payload key into a readable label. Known snapshot fields use their curated label;
 * anything else falls back to sentence case (`shouldZeroOut` -> "Should zero out").
 */
export const humanizeKey = (key: string): string =>
  SNAPSHOT_FIELD_LABELS[key] ?? upperFirst(lowerCase(key));

/**
 * Render one scalar value from a UoW detail payload.
 *
 * Money formatting is per key, matching the snapshot rules: most amounts are dollars, anything named
 * `*InCents` is cents. Unknown numeric keys are printed as-is rather than guessed at.
 */
export const formatDetailScalar = (key: string, value: unknown): string => {
  if (value === null || value === undefined) {
    return NOT_RECORDED;
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (Array.isArray(value)) {
    return value.length ? value.map((item) => String(item)).join(', ') : EMPTY_LIST;
  }

  if (typeof value === 'number') {
    if (DOLLAR_FIELDS.has(key) || DETAIL_DOLLAR_KEYS.has(key)) {
      return formatCurrency(value);
    }

    if (CENT_FIELDS.has(key) || key.endsWith('InCents')) {
      return formatCurrency(value / CENTS_IN_A_DOLLAR);
    }

    return String(value);
  }

  return String(value);
};

/*
 * lodash `isPlainObject`, not a bare `typeof === 'object'` check: a detail payload is an open bag
 * from a log, and a Date or Map would pass the naive check and then render as an empty group.
 * lodash types it as plain `boolean`, so the narrowing lives here — one named predicate rather than
 * a cast at the use site.
 */
const isDetailGroup = (value: unknown): value is Record<string, unknown> => isPlainObject(value);

/** A UoW detail entry flattened for display: either a single row, or a labelled group of rows. */
export type DetailEntry = {
  label: string;
  value: string | null;
  children: { label: string; value: string }[];
};

/**
 * Flatten a UoW `details` payload into displayable entries.
 *
 * UoW payloads have no fixed schema and some carry a nested object (`orderCalculations` logs
 * `computed: { orderTotal, balanceDue, ... }`). Those are expanded into labelled child rows rather
 * than dumped as raw JSON.
 */
export const toDetailEntries = (details: Record<string, unknown>): DetailEntry[] =>
  Object.entries(details).map(([key, value]) => {
    if (isDetailGroup(value)) {
      return {
        label: humanizeKey(key),
        value: null,
        children: Object.entries(value).map(
          ([childKey, childValue]) => ({
            label: humanizeKey(childKey),
            value: formatDetailScalar(childKey, childValue),
          }),
        ),
      };
    }

    return { label: humanizeKey(key), value: formatDetailScalar(key, value), children: [] };
  });

export const formatDuration = (durationMs: number | null): string => {
  if (durationMs === null) {
    return NOT_RECORDED;
  }

  return durationMs < 1000 ? `${durationMs}ms` : `${(durationMs / 1000).toFixed(2)}s`;
};
